'use es6';

import { useMemo, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import * as CustomPropTypes from '../../../constants/PropTypes';
import Immutable from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Small from 'UIComponents/elements/Small';
import UIButton from 'UIComponents/button/UIButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import * as FilterKey from '../../../constants/search/FilterKey';
import FilterTag from './FilterTag';
import FilterTagText from './FilterTagText';
import MobileFilterBar from 'marketplace-ui-common/components/mobile/MobileFilterBar';
import MobileTagButton from 'marketplace-ui-common/components/mobile/MobileTagButton';
import { getTranslatedSortText } from '../../../utils/i18n';
import I18n from 'I18n';
import { createElement as _createElement } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ResultsFilters = ({
  className,
  filters,
  isMobile,
  onClearFilters,
  onFilterChange,
  onSortChange,
  startSlot,
  trackInteraction,
  validFilterTagKeys
}) => {
  const {
    [FilterKey.CATEGORY]: activeCategory
  } = filters;
  const handleCloseFilterTag = useCallback((filterKey, value) => {
    const filterValue = filters.get(filterKey);
    onFilterChange(filterKey, Immutable.Set.isSet(filterValue) ? filterValue.delete(value) : null);
  }, [filters, onFilterChange]);
  const renderedTags = useMemo(() => {
    return validFilterTagKeys.reduce((final, key) => {
      let value = filters.get(key);
      if (!Immutable.Set.isSet(value)) {
        value = Immutable.Set.of(value);
      }
      let tagProps = {};
      if (key === FilterKey.CATEGORY_FEATURES) {
        tagProps = {
          activeCategory
        };
      }
      value.forEach(val => {
        final.push( /*#__PURE__*/_createElement(FilterTag, Object.assign({}, tagProps, {
          key: `${key}:${val}`,
          filterKey: key,
          onCloseClick: () => {
            handleCloseFilterTag(key, val);
            trackInteraction('close filter tag', {
              filter: key,
              value: val
            });
          },
          value: val
        })));
      });
      return final;
    }, []);
  }, [activeCategory, filters, handleCloseFilterTag, trackInteraction, validFilterTagKeys]);
  const renderedMobileTags = useMemo(() => {
    return validFilterTagKeys.reduce((final, key) => {
      let value = filters.get(key);
      if (!Immutable.Set.isSet(value)) {
        value = Immutable.Set.of(value);
      }
      let tagProps = {};
      if (key === FilterKey.CATEGORY_FEATURES) {
        tagProps = {
          activeCategory
        };
      }
      value.forEach(val => {
        final.push( /*#__PURE__*/_jsx(MobileTagButton, {
          closeable: true,
          onClick: () => {
            handleCloseFilterTag(key, val);
            trackInteraction('close filter tag', {
              filter: key,
              value: val
            });
          },
          children: /*#__PURE__*/_jsx(FilterTagText, Object.assign({}, tagProps, {
            filterKey: key,
            value: val
          }))
        }, `${key}:${val}`));
      });
      return final;
    }, []);
  }, [activeCategory, filters, handleCloseFilterTag, trackInteraction, validFilterTagKeys]);
  const renderedDefaultFilterBar = useMemo(() => {
    return /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      wrap: "wrap",
      children: [renderedTags, /*#__PURE__*/_jsx(UIButton, {
        className: "m-left-3",
        onClick: evt => {
          onClearFilters(evt);
          trackInteraction('clear all filters', {
            filterCount: renderedTags.length
          });
        },
        use: "link",
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "appMarketplace.storefront.results.resultsPage.clearFiltersText"
          })
        })
      })]
    });
  }, [onClearFilters, trackInteraction, renderedTags]);
  const clearSort = useCallback((sortKey, sortOrder) => {
    onSortChange(sortKey, sortOrder);
  }, [onSortChange]);
  const sortTag = useMemo(() => {
    const sortKey = filters.get(FilterKey.SORT_FIELD);
    const sortOrder = filters.get(FilterKey.SORT_ORDER);
    const sortText = getTranslatedSortText(sortKey, sortOrder);
    const tagText = I18n.text(`appMarketplace.storefront.results.sort.filterTagPreface`, {
      sortText
    });
    return sortKey !== null && /*#__PURE__*/_jsx(MobileTagButton, {
      onClick: clearSort,
      closeable: true,
      children: tagText
    });
  }, [clearSort, filters]);
  if (!renderedTags.length && !isMobile) {
    return null;
  }
  return isMobile ? /*#__PURE__*/_jsx(MobileFilterBar, {
    className: className,
    startSlot: startSlot,
    middleSlot: /*#__PURE__*/_jsxs(Fragment, {
      children: [renderedMobileTags, sortTag]
    })
  }) : /*#__PURE__*/_jsx(UIFlex, {
    align: "center",
    wrap: "wrap",
    className: className,
    children: renderedDefaultFilterBar
  });
};
ResultsFilters.propTypes = {
  className: PropTypes.string,
  filters: CustomPropTypes.storefrontFilters.isRequired,
  isMobile: PropTypes.bool,
  onClearFilters: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func,
  sortOnly: PropTypes.bool,
  startSlot: PropTypes.node,
  trackInteraction: PropTypes.func.isRequired,
  validFilterTagKeys: ImmutablePropTypes.orderedSetOf(PropTypes.oneOf(Object.values(FilterKey))).isRequired
};
export default ResultsFilters;